import React, { useEffect, useRef, useState } from "react";
import { doc, collection, getFirestore, updateDoc, setDoc, CollectionReference, deleteField } from "firebase/firestore";
import { auth as rebuildAuth } from "../../../services/authService";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { v4 as uuidv4 } from "uuid";
import "../LineItemDBCreationToolPage.scss";
import { observer } from "mobx-react";
import SharedUtils from "../../../shared/common/SharedUtils";
import { logDebug, logError } from "../../../shared/common/logger";
import LoadingAnimation from "../../../components/ui/utils/LoadingAnimation";
import { toast } from "react-toastify";
import { getMappingStore } from "../MappingTool/utils/MappingUtils";
import getMappingToolStore from "../MappingTool/data/MappingToolStore";
import { LineItemCategory } from "@rebuild/shared";
import MappingTopBox from "../MappingTool/components/MappingTopBox";
import LineItemInfoModal from "../LineItemInfoModal";
import { AcceptableValueDependency, ACTION_VARIABLE_VALUES, IDatabaseLineItem, LineItemElementProps, LineItemVariable, NON_AUTO_POPULATED_ACTION_VARIABLE_VALUES } from "./types";
import { recalculateElementIndexes } from "./utils/lineItemUtils";
import LineItemTopInput from "./components/LineItemTopInput";
import LineItemEditor from "./components/LineItemEditor";
import LineItemListControls from "./components/LineItemListControls";
import LineItemListItem from "./components/LineItemListItem";
import SavePreviewModal from "./components/SavePreviewModal";
import VariableCreationPopup from "./components/VariableCreationPopup";
import { LineItemPreviewPanel } from "./components/LineItemPreviewPanel";
import { XactimateSyncReviewModal } from "./components/XactimateSyncReview";
import { ScopeTag } from "../MappingTool/utils/ScopeTag";
import { fetchXactimateMappingsForLineItem } from "../../../utils/xactimateMappingsAPI";
import { validateXactimateMappings } from "../../../utils/validateXactimateMappings";

const createEmptyLineItem = (): LineItemElementProps[] => {
	return [{ value: "", type: "text" }];
};

const LineItemDBCreationToolPage: React.FC<{ lineItemToEdit?: IDatabaseLineItem }> = observer(({ lineItemToEdit }) => {
	const mappingStore = getMappingStore();
	const firestoreToolsCollectionRef = collection(useFirestore(), SharedUtils.getDbPath("tools")) as CollectionReference<IDatabaseLineItem>;
	const lineItemsDataFromDataBase = useFirestoreCollectionData<IDatabaseLineItem>(firestoreToolsCollectionRef, { idField: "id" });
	const [lineItemsToWorkWith, setLineItemsToWorkWith] = useState<IDatabaseLineItem[]>([]);
	const [lineItemToProcess, setLineItemToProcess] = useState<LineItemElementProps[]>(createEmptyLineItem());
	const [inputValue, setInputValue] = useState<string>("");
	const [showVariableCreationPopup, setShowVariableCreationPopup] = useState<any>(null);
	const [selectedText, setSelectedText] = useState<string>("");
	const [variablesWindowVariables, setVariablesWindowVariables] = useState<string[]>([]);
	const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [lineItemId, setLineItemId] = useState<string | null>(null);
	const [existingVariables, setExistingVariables] = useState<LineItemVariable[]>([]);
	const currentLineProcessingDiv = useRef<HTMLDivElement>(null);
	const [searchValue, setSearchValue] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("");
	const [scopeFilter, setScopeFilter] = useState<string>("");
	const [sortValue, setSortValue] = useState<"asc" | "desc">("asc");
	const inputVariableRef = useRef<HTMLInputElement>(null);
	const [allowEmptyValue, setAllowEmptyValue] = useState<boolean>(true);
	const [conditional, setConditional] = useState<boolean>(false);
	const [defaultVariableValue, setDefaultVariableValue] = useState<string>("");
	const [showHamburgerMenu, setShowHamburgerMenu] = useState<boolean>(false);
	const [showRecycleBin, setShowRecycleBin] = useState<boolean>(false);
	const [previewIndex, setPreviewIndex] = useState<number>(-1);
	const [showEditTextInput, setShowEditTextInput] = useState<boolean>(false);
	const [editingLineValue, setEditingLineValue] = useState<string>("");
	const [selectedCategory, setSelectedCategory] = useState<string>(LineItemCategory.GENERAL);
	const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
	const [selectedLineItemForInfo, setSelectedLineItemForInfo] = useState<IDatabaseLineItem | null>(null);
	const [savedLineItemForSync, setSavedLineItemForSync] = useState<IDatabaseLineItem | null>(null);
	const [pendingNonDraftScopes, setPendingNonDraftScopes] = useState<string[]>([]);
	const syncReviewIsFromSaveFlow = useRef(false);
	const idToIndexMap = useRef(new Map<string, number>());

	// Recalculate element indexes for dependencies based on old and new elements
	const updateElementsWithRecalculation = (newElements: LineItemElementProps[]) => {
		setLineItemToProcess((prev) =>
			newElements.length !== prev.length ? recalculateElementIndexes(prev, newElements) : newElements
		);
	};

	useEffect(() => {
		if (!lineItemToEdit) {
			setLineItemToProcess(createEmptyLineItem());
			setLineItemId(null);
			setSelectedCategory(LineItemCategory.GENERAL);
			setSelectedScopes([]);
			return;
		}
		setLineItemToProcess(lineItemToEdit!.elements);
		setLineItemId(lineItemToEdit!.id);
		setSelectedCategory(lineItemToEdit!.category ?? "Other");
		setSelectedScopes(lineItemToEdit!.scopes ?? []);
	}, [lineItemToEdit]);

	async function dateAndIndexAllLineItems() {
		if (location.href.includes("allowSetData=1")) {
			const allLineItems = lineItemsToWorkWith;
			const promises = allLineItems.map(async (li, index) => {
				const docRef = doc(firestoreToolsCollectionRef, li.id);
				await updateDoc(docRef, {
					createTime: Date.now() - index * 1000 * 60,
					index: allLineItems.length - index,
				});
			});
			await Promise.all(promises);
		}
	}

	const addLineItemForProcessing = () => {
		setLineItemId(null);
		setEditingIndex(null);
		if (inputValue.trim() === "") {
			dateAndIndexAllLineItems();
			return;
		}
		setTimeout(() => {
			const inputValueWithSingleSpace = ` ${inputValue.replace(/\s+/g, " ").trim()} `;
			const newLineItem = populateStringWithExistingElemens(inputValueWithSingleSpace);
			setLineItemToProcess(newLineItem);
			setLineItemId(uuidv4());
			setSelectedCategory(LineItemCategory.GENERAL);
			setSelectedScopes([]);
			setInputValue("");
		}, 100);
	};

	useEffect(() => {
		// merge the text values that are next to each other
		const newValues: LineItemElementProps[] = [lineItemToProcess[0]];
		let haveChange = false;
		for (let i = 1; i < lineItemToProcess.length; i++) {
			const current = lineItemToProcess[i];
			if (current.type === "variable") {
				newValues.push(current);
				continue;
			} else if (current.type === "text" && newValues[newValues.length - 1].type === "text") {
				haveChange = true;
				newValues[newValues.length - 1].value += " " + current.value;
				newValues[newValues.length - 1].value = newValues[newValues.length - 1].value.replace(/\s+/g, " ").trim();
			} else {
				newValues.push(current);
			}
		}
		if (haveChange) {
			updateElementsWithRecalculation(newValues);
		}
	}, [lineItemToProcess]);

	useEffect(() => {
		if (!mappingStore.loadingData) {
			mappingStore.loadingData = true;
		}
	}, []);

	useEffect(() => {
		if (lineItemsDataFromDataBase.status === "success") {
			if (mappingStore.loadingData) {
				mappingStore.loadingData = false;
			}
		}
	}, [lineItemsDataFromDataBase]);

	useEffect(() => {
		const div = currentLineProcessingDiv.current;
		const handleMouseUp = (e: any) => {
			e.preventDefault();
			e.stopPropagation();
			const selection = window.getSelection();
			if (selection?.toString().trim() && !showEditTextInput) {
				let selectedText = selection?.toString().trim();
				const index = lineItemToProcess.findIndex((i) => i.value.toLocaleLowerCase().includes(selectedText.toLocaleLowerCase()));
				if (index === -1) {
					return;
				}
				const startIndex = lineItemToProcess[index].value.toLocaleLowerCase().indexOf(selectedText.toLocaleLowerCase());
				let start = startIndex;
				while (lineItemToProcess[index].value[start] !== " " && lineItemToProcess[index].value[start] !== "\t" && start > 0) {
					start--;
				}
				let end = startIndex + selectedText.length;
				while (
					lineItemToProcess[index].value[end] !== " " &&
					lineItemToProcess[index].value[end] !== "\t" &&
					end < lineItemToProcess[index].value.length
				) {
					end++;
				}
				selectedText = lineItemToProcess[index].value.substring(start, end);

				logDebug("selection", selection?.toString().trim());
				setShowVariableCreationPopup({
					top: selection?.getRangeAt(0).getBoundingClientRect().bottom,
					left: selection?.getRangeAt(0).getBoundingClientRect().left + selection?.getRangeAt(0).getBoundingClientRect().width / 2,
				});
				setVariablesWindowVariables([selectedText.trim()]);
				setSelectedText(selectedText.trim());
			}
		};
		div?.removeEventListener("mouseup", handleMouseUp);
		div?.addEventListener("mouseup", handleMouseUp);
		return () => {
			div?.removeEventListener("mouseup", handleMouseUp);
		};
	}, [lineItemToProcess, showEditTextInput]);

	useEffect(() => {
		const newValues: LineItemVariable[] = [];
		lineItemsDataFromDataBase?.data?.forEach((item) => {
			item.elements?.forEach((element) => {
				if (element.type === "variable") {
					const index = newValues.findIndex((i) => i.value?.toLowerCase() === element.value?.toLowerCase());
					if (index !== -1) {
						const existing = newValues[index];
						const existingValuesLowerCase = existing.acceptableValues?.map((i) => i.toLowerCase());
						const newAcceptableValues = element.acceptableValues!.filter((i) => !existingValuesLowerCase.includes(i.toLowerCase()));
						if (!newAcceptableValues.length) {
							return;
						}
					}
					newValues.push({
						...element,
						acceptableValues: element.acceptableValues!,
					});
				}
			});
		});
		setExistingVariables(newValues);
	}, [lineItemsDataFromDataBase]);

	useEffect(() => {
		if (lineItemsDataFromDataBase.data && lineItemsDataFromDataBase.data.length) {
			const newValues = [...lineItemsDataFromDataBase.data]
				.map((item) => {
					if (item.index !== undefined) {
						idToIndexMap.current.set(item.id, item.index!);
					}
					return item;
				})
				.filter((item) => (showRecycleBin ? item.deleted : !item.deleted))
				.filter((item) => item.originalLineItem && (!searchValue || item.originalLineItem.toLowerCase().includes(searchValue.toLowerCase())))
				.filter((item) => !categoryFilter || item.category === categoryFilter)
				.filter((item) => !scopeFilter || (item.scopes && item.scopes.includes(scopeFilter)));

			if (lineItemsDataFromDataBase.data[0].createTime) {
				newValues.sort((a, b) => (b.createTime ?? 0) - (a.createTime ?? 0));
			} else {
				if (sortValue === "asc") {
					newValues.sort((a, b) => a.originalLineItem.localeCompare(b.originalLineItem));
				} else {
					newValues.sort((a, b) => b.originalLineItem.localeCompare(a.originalLineItem));
				}
			}
			setLineItemsToWorkWith(newValues);
		}
	}, [lineItemsDataFromDataBase, sortValue, searchValue, categoryFilter, scopeFilter, showRecycleBin]);

	const populateStringWithExistingElemens = (
		line: string,
		priorityAcceptableValues?: { value: string; variable: LineItemVariable, valueDependencies?: { [value: string]: AcceptableValueDependency[] } }[]
	): LineItemElementProps[] => {
		if (editingIndex !== null) {
			throw new Error("Editing index should be null");
		}
		let newLineItem: LineItemElementProps[] = [
			{
				value: line,
				type: "text",
			},
		];

		const actionVariables = existingVariables.filter((variable) =>
			(variable.acceptableValues ?? []).some((v) => ACTION_VARIABLE_VALUES.includes(v.trim().toLowerCase()))
		);
		const allAcceptableValues: { value: string; variable: LineItemVariable; valueDependencies?: { [value: string]: AcceptableValueDependency[] } }[] = [];
		actionVariables.forEach((variable) => {
			(variable.acceptableValues ?? []).forEach((value) => {
				if (NON_AUTO_POPULATED_ACTION_VARIABLE_VALUES.includes(value.trim().toLowerCase())) return;
				allAcceptableValues.push({ value: ` ${value.trim()} `.toLowerCase(), variable });
			});
		});
		let allAcceptableValuesSortedByLength = allAcceptableValues.sort((a, b) => b.value.length - a.value.length);
		if (priorityAcceptableValues) {
			const priorityValues = priorityAcceptableValues.map((i) => ({
				value: ` ${i.value.trim()} `.toLowerCase(),
				variable: i.variable,
				valueDependencies: i.valueDependencies,
			}));
			allAcceptableValuesSortedByLength = [...priorityValues, ...allAcceptableValuesSortedByLength];
		}
		allAcceptableValuesSortedByLength.forEach((acceptableValue) => {
			newLineItem.forEach((element, i) => {
				if (element.type === "text") {
					if (` ${element.value} `.toLowerCase().includes(acceptableValue.value)) {
						newLineItem = createVariableFromSelectedText(
							acceptableValue.value,
							newLineItem,
							acceptableValue.variable.acceptableValues,
							!!acceptableValue.variable.allowEmptyValue,
							!!acceptableValue.variable.conditional,
							acceptableValue.variable.defaultValue ?? "",
							acceptableValue.valueDependencies
						);
						return;
					}
				}
			});
		});
		return newLineItem;
	};

	const updateDeletedState = async (id: string, deleted: boolean) => {
		const docRef = doc(firestoreToolsCollectionRef, id);
		if (deleted) {
			await updateDoc(docRef, {
				deleted: true,
				deletedBy: rebuildAuth.currentUser!.uid,
				deletedTimestamp: Date.now(),
			});
		} else {
			await updateDoc(docRef, {
				deleted: false,
				deletedBy: deleteField(),
				deletedTimestamp: deleteField(),
			});
		}
	};

	const createVariableFromSelectedText = (
		variableValue: string,
		existingLineItem: LineItemElementProps[],
		acceptableValues: string[],
		_allowEmptyValue: boolean,
		_conditional: boolean,
		_defaultValue: string,
		_valueDependencies?: { [value: string]: AcceptableValueDependency[] }
	): LineItemElementProps[] => {
		function normalizeVariables(arr: string[]): string[] {
			return arr
				.map((i) => i.replace(/\s+/g, " ").trim())
				.filter((v, i, a) => SharedUtils.arrayIndexOfCaseInsensitive(a, v) === i && v.trim().length > 0);
		}
		variableValue = ` ${variableValue.replace(/\s+/g, " ").trim()} `;
		existingLineItem = existingLineItem.map((ele) => ({
			...ele,
			value: ` ${ele.value.replace(/\s+/g, " ").trim()} `,
		}));
		if (editingIndex !== null) {
			const existingElement = existingLineItem[editingIndex];
			const newValues = [...existingLineItem];
			newValues.splice(editingIndex, 1, {
				value: variableValue,
				type: "variable",
				acceptableValues: normalizeVariables([...variablesWindowVariables]),
				allowEmptyValue: _allowEmptyValue,
				conditional: _conditional,
				defaultValue: _defaultValue,
				...(existingElement.valueDependencies ? { valueDependencies: existingElement.valueDependencies } : {}),
			});
			return newValues;
		}
		const index = existingLineItem.findIndex((i) => i.value.toLocaleLowerCase().includes(variableValue.toLocaleLowerCase()));
		if (index === -1) {
			return existingLineItem;
		}

		const parts = SharedUtils.splitIgnoreCase(existingLineItem[index].value, variableValue);
		const newValues: LineItemElementProps[] = [];
		if (parts[0].trim()) {
			newValues.push({
				value: ` ${parts[0].trim()} `,
				type: "text",
			});
		}
		newValues.push({
			value: variableValue,
			type: "variable",
			acceptableValues: acceptableValues ? [...acceptableValues] : normalizeVariables([...variablesWindowVariables]),
			allowEmptyValue: !!_allowEmptyValue,
			conditional: !!_conditional,
			defaultValue: _defaultValue,
			...(_valueDependencies ? { valueDependencies: _valueDependencies } : {}),
		});
		if (parts[1].trim()) {
			newValues.push({
				value: ` ${parts[1].trim()} `,
				type: "text",
			});
		}
		logDebug("newValues", newValues);
		const newLineItems = [...existingLineItem];
		newLineItems.splice(index, 1, ...newValues);
		return newLineItems;
	};

	const variableModalCallback = (command: "delete" | "cancel" | "create") => {
		if (command === "delete") {
			if (editingIndex !== null) {
				const newValues = [...lineItemToProcess];
				newValues[editingIndex!] = { value: newValues[editingIndex!].value, type: "text" };
				updateElementsWithRecalculation(newValues);
			}
		} else if (command === "cancel") {
		} else if (command === "create") {
			const newElements = createVariableFromSelectedText(selectedText, lineItemToProcess, variablesWindowVariables, allowEmptyValue, conditional, defaultVariableValue);
			updateElementsWithRecalculation(newElements);
		}
		window.getSelection()?.removeAllRanges();
		setVariablesWindowVariables([]);
		setShowVariableCreationPopup(null);
		setDefaultVariableValue("");
		setEditingIndex(null);
		setAllowEmptyValue(true);
		setConditional(false);
	};

	async function fixDatabase() {
		const yesNo = window.confirm("Are you sure you want to fix the database?");
		if (!yesNo) {
			return;
		}

		const firestore = getFirestore();
		mappingStore.loadingData = true;
		try {
			const allLineItems = lineItemsDataFromDataBase.data;
			for (const lineItem of allLineItems) {
				let errorConfigured = false;
				if (Array.isArray(lineItem.elements)) {
					for (const element of lineItem.elements) {
						if (
							element.type === "variable" &&
							(element.value.trim() === "replace" || element.value.trim() === "remove" || element.value.trim() === "remove and replace")
						) {
							if (element.allowEmptyValue && element.defaultValue) {
								errorConfigured = true;
								element.allowEmptyValue = false;
							}
						}
						if (
							element.type === "variable" &&
							element.acceptableValues?.find((e) => e.toLowerCase() === "very high") &&
							element.acceptableValues?.find((e) => e.toLowerCase() === "av++") &&
							element.acceptableValues?.find((e) => e.toLowerCase() === "high")
						) {
							element.acceptableValues = element.acceptableValues.filter((e) => e.toLowerCase() !== "standard");
							element.acceptableValues.push("standard");
							errorConfigured = true;
						}

						if (
							element.type === "variable" &&
							["av", "av+", "av++", "av+++", "av-"].find((elementToRemove) =>
								element.acceptableValues?.find((a) => a.toLowerCase().trim() === elementToRemove)
							)
						) {
							element.acceptableValues = element.acceptableValues!.filter(
								(e) => !["av", "av+", "av++", "av+++", "av-"].includes(e.toLowerCase().trim())
							);
							errorConfigured = true;
						}
					}
				}

				if (errorConfigured) {
					try {
						const docRef = doc(firestore, SharedUtils.getDbPath("tools"), lineItem.id);
						await setDoc(
							docRef,
							{
								elements: [...lineItem.elements],
							},
							{
								merge: true,
							}
						);
						toastNotify("Line item fixed successfully");
					} catch (err) {
						toastNotify("Error fixing line item", true);
					}
				}
			}
		} catch (err) {}
		mappingStore.loadingData = false;
	}

	if (lineItemsDataFromDataBase.status === "error") {
		return <div>Error Connecting to Database: {lineItemsDataFromDataBase.error?.message}</div>;
	}

	const toastNotify = (text: string, error?: boolean) => {
		(error ? toast.error : toast.success)(
			<div className="flex items-center">
				<div className="mr-4 ml-4 text-gray-900 pr-16">{text}</div>
			</div>
		);
	};

	const handleVariableClick = (ind: number, element: LineItemElementProps, rect: DOMRect) => {
		if (editingIndex === ind) {
			variableModalCallback("cancel");
		} else {
			setDefaultVariableValue(element.defaultValue ?? "");
			setEditingIndex(ind);
			setSelectedText(element.value);
			setVariablesWindowVariables([...(element.acceptableValues || [])]);
			setAllowEmptyValue(!!element.allowEmptyValue);
			setConditional(!!element.conditional);
			setShowVariableCreationPopup({
				top: rect.bottom + 40,
				left: rect.left + rect.width / 2,
			});
		}
	};

	const handleEditTextToggle = () => {
		setShowEditTextInput((showing: boolean) => {
			if (showing) {
				if (
					lineItemToProcess
						.map((element) => element.value)
						.join(" ")
						.replace(/\s+/g, " ") !== editingLineValue
				) {
					const line = populateStringWithExistingElemens(
						editingLineValue,
						lineItemToProcess
							.filter((i) => i.type === "variable")
							.map((i) => ({ value: i.value, variable: i as LineItemVariable, valueDependencies: i.valueDependencies }))
					);
					updateElementsWithRecalculation(line);
				}
			} else {
				setEditingLineValue(
					lineItemToProcess
						.map((element) => element.value)
						.join(" ")
						.replace(/\s+/g, " ")
				);
			}
			return !showing;
		});
	};

	const resetEditorState = () => {
		setShowPreviewModal(false);
		setLineItemToProcess(createEmptyLineItem());
		setLineItemId(null);
		setSelectedCategory(LineItemCategory.GENERAL);
		setSelectedScopes([]);
	};

	const handleSyncReviewDismiss = () => {
		const wasFromSaveFlow = !!syncReviewIsFromSaveFlow.current;
		const hadPendingScopes = pendingNonDraftScopes.length > 0;
		syncReviewIsFromSaveFlow.current = false;
		setSavedLineItemForSync(null);
		setPendingNonDraftScopes([]);
		if (hadPendingScopes) {
			toastNotify("Line item was not saved to the selected scope(s) — map all permutations to apply them.", true);
		}
		if (wasFromSaveFlow) {
			resetEditorState();
		}
	};

	const applyPendingNonDraftScopes = async (savedItem: IDatabaseLineItem, scopesToApply: string[]) => {
		const firestore = getFirestore();
		const docRef = doc(firestore, SharedUtils.getDbPath("tools"), savedItem.id);
		const mergedScopes = Array.from(new Set([...(savedItem.scopes ?? []), ...scopesToApply]));
		await setDoc(
			docRef,
			{
				scopes: mergedScopes,
				lastModifiedBy: rebuildAuth.currentUser!.uid,
				lastModifiedAt: Date.now(),
			},
			{ merge: true }
		);
		setSavedLineItemForSync((prev) => (prev ? { ...prev, scopes: mergedScopes } : prev));
		setPendingNonDraftScopes([]);
	};

	const handleSaveLineItem = async () => {
		const firestore = getFirestore();
		const docRef = doc(firestore, SharedUtils.getDbPath("tools"), lineItemId!);
		logDebug("lineItemToProcess", lineItemId, lineItemToProcess);
		mappingStore.loadingData = true;
		try {
			const nonDraftScopes = selectedScopes.filter((s) => s !== ScopeTag.DRAFT);
			let scopesToPersist = selectedScopes;
			let scopesPendingMapping: string[] = [];

			if (nonDraftScopes.length > 0 && import.meta.env.VITE_XACTIMATE_SYNC_ENABLED === "true") {
				const probeItem: IDatabaseLineItem = {
					id: lineItemId!,
					index: 0,
					createTime: 0,
					category: selectedCategory,
					originalLineItem: lineItemToProcess.map((i) => i.value.trim()).join(" "),
					elements: lineItemToProcess,
				};
				try {
					const existingMappings = await fetchXactimateMappingsForLineItem(lineItemId!);
					const validation = validateXactimateMappings(probeItem, existingMappings);
					if (validation.unmappedPermutations.length > 0) {
						scopesToPersist = selectedScopes.filter((s) => s === ScopeTag.DRAFT);
						scopesPendingMapping = nonDraftScopes;
					}
				} catch {
					scopesToPersist = selectedScopes.filter((s) => s === ScopeTag.DRAFT);
					scopesPendingMapping = nonDraftScopes;
					toastNotify("Could not verify Xactimate mappings — saving to draft scopes only.", true);
				}
			}

			const savedItem: IDatabaseLineItem = {
				id: lineItemId!,
				index: idToIndexMap.current.has(lineItemId!)
					? idToIndexMap.current.get(lineItemId!)
					: Math.max(...lineItemsDataFromDataBase.data.map((i) => i.index ?? 0)) + 1,
				createTime: new Date().getTime(),
				category: selectedCategory,
				...(scopesToPersist.length > 0 ? { scopes: scopesToPersist } : {}),
				originalLineItem: lineItemToProcess.map((i) => i.value.trim()).join(" "),
				elements: lineItemToProcess,
				lastModifiedBy: rebuildAuth.currentUser!.uid,
				lastModifiedAt: Date.now(),
			};
			await setDoc(docRef, { ...savedItem, scopes: scopesToPersist }, { merge: true });
			toastNotify("Line item saved successfully");
			mappingStore.loadingData = false;
			if (import.meta.env.VITE_XACTIMATE_SYNC_ENABLED === "true") {
				syncReviewIsFromSaveFlow.current = true;
				setPendingNonDraftScopes(scopesPendingMapping);
				setSavedLineItemForSync(savedItem);
			} else {
				resetEditorState();
			}
		} catch (err) {
			toastNotify("Error saving line item", true);
			mappingStore.loadingData = false;
			resetEditorState();
		}
	};

	return (
		<div className={"w-full h-full" + (lineItemToEdit && " bg-white")}>
			{!lineItemToEdit && (
				<>
					<MappingTopBox />
					<LineItemTopInput
						inputValue={inputValue}
						onInputChange={setInputValue}
						onProcess={addLineItemForProcessing}
					/>
				</>
			)}
			{/*  */}
			<LineItemEditor
				lineItemToProcess={lineItemToProcess}
				lineItemToEdit={lineItemToEdit}
				showEditTextInput={showEditTextInput}
				editingLineValue={editingLineValue}
				editingIndex={editingIndex}
				containerRef={currentLineProcessingDiv}
				onEditingLineValueChange={setEditingLineValue}
				onEditTextToggle={handleEditTextToggle}
				onVariableClick={handleVariableClick}
				onSaveClick={() => setShowPreviewModal(true)}
				onCopyAll={() => {
					const dataToClipboard = JSON.stringify(lineItemsToWorkWith);
					navigator.clipboard.writeText(dataToClipboard);
					alert("Data copied to clipboard");
				}}
				onFixDB={fixDatabase}
			/>

			{lineItemToProcess.some((e) => e.type === "variable") && (
				<div className="px-12 pb-8 flex flex-wrap items-start">
					<LineItemPreviewPanel
						lineItemToProcess={lineItemToProcess}
						isEditing={true}
						onElementsChange={setLineItemToProcess}
					/>
				</div>
			)}

			{!lineItemToEdit && (
				<>
					<LineItemListControls
						lineItemCount={lineItemsToWorkWith.length}
						totalCount={lineItemsDataFromDataBase.data?.filter((i) => !i.deleted).length ?? 0}
						sortValue={sortValue}
						onSortChange={setSortValue}
						categoryFilter={categoryFilter}
						onCategoryFilterChange={setCategoryFilter}
						scopeFilter={scopeFilter}
						onScopeFilterChange={setScopeFilter}
						onSearchChange={setSearchValue}
						showRecycleBin={showRecycleBin}
						onRecycleBinClose={() => setShowRecycleBin(false)}
						showHamburgerMenu={showHamburgerMenu}
						onHamburgerToggle={() => setShowHamburgerMenu(!showHamburgerMenu)}
						onShowRecycleBin={() => {
							setShowRecycleBin(true);
							setShowHamburgerMenu(false);
						}}
					>
						{lineItemsToWorkWith.length > 0 &&
							lineItemsToWorkWith.map((item, index) => {
								if (item.id === "test" || item.id === "mapping_tool") {
									return null;
								}
								return (
									<LineItemListItem
										key={item.id}
										item={item}
										index={index}
										isExpanded={previewIndex === index}
										showRecycleBin={showRecycleBin}
										showCopyButton={location.href.includes("allowCopy=1")}
										showXactimateButton={import.meta.env.VITE_XACTIMATE_SYNC_ENABLED === "true"}
										onToggleExpand={() => setPreviewIndex((v) => (v === index ? -1 : index))}
										onEdit={() => {
											setLineItemToProcess(item.elements);
											setLineItemId(item.id);
											setSelectedCategory(item.category ?? LineItemCategory.GENERAL);
											setSelectedScopes(item.scopes ?? []);
										}}
										onDelete={() => updateDeletedState(item.id, true)}
										onRestore={() => updateDeletedState(item.id, false)}
										onCopy={() => {
											const itemToCopy = JSON.stringify(item);
											navigator.clipboard.writeText(itemToCopy);
											toastNotify("Line item copied to clipboard");
										}}
										onInfoClick={() => setSelectedLineItemForInfo(item)}
										onXactimateClick={() => {
											syncReviewIsFromSaveFlow.current = false;
											setSavedLineItemForSync(item);
										}}
									/>
								);
							})}
					</LineItemListControls>

					{SharedUtils.showDebug && (
						<div className="flex flex-col justify-start items-start">
							<textarea id="copy-line-item" className="mt-2 border border-gray-400 w-[800px] h-[100px]" />
							<button
								onClick={async () => {
									const copyText = document.getElementById("copy-line-item") as HTMLTextAreaElement;
									if (copyText.value.trim()) {
										mappingStore.loadingData = true;
										try {
											const val = JSON.parse(copyText.value.trim()) as IDatabaseLineItem;
											if (!val.id || !val.elements || !val.category) {
												throw new Error("Invalid JSON");
											}
											const firestore = getFirestore();
											const docRef = doc(firestore, SharedUtils.getDbPath("tools"), val.id);
											await setDoc(docRef, val, {
												merge: false,
											});
										} catch (err) {
											toastNotify("Invalid JSON", true);
										}
										copyText.value = "";
										mappingStore.loadingData = false;
									}
								}}
							>
								Save Line Item to Database
							</button>
							<button
								onClick={async () => {
									const copyText = document.getElementById("copy-line-item") as HTMLTextAreaElement;
									if (copyText.value.trim()) {
										const areYouSure = window.confirm("Are you sure you want to save all line items to the database?");
										if (!areYouSure) {
											return;
										}
										mappingStore.loadingData = true;
										try {
											const vals = JSON.parse(copyText.value.trim()) as IDatabaseLineItem[];
											if (!Array.isArray(vals)) {
												throw new Error("Invalid JSON");
											}
											const firestore = getFirestore();
											for (const val of vals) {
												if (!val.id || !val.elements || !val.category) {
													logError("Invalid JSON", val);
													continue;
												}
												const docRef = doc(firestore, SharedUtils.getDbPath("tools"), val.id);
												await setDoc(docRef, val, {
													merge: false,
												});
											}
										} catch (err) {
											toastNotify("Invalid JSON", true);
										}
										copyText.value = "";
										mappingStore.loadingData = false;
									}
								}}
							>
								Save All Line Items to Database
							</button>
							<button
								onClick={() => {
									const generatedData: any = {};
									lineItemsDataFromDataBase.data.forEach((item) => {
										if (item.deleted) return;
										generatedData[item.id] = {
											elements: item.elements
												?.map((element) => (element.type === "variable" ? element.acceptableValues : null))
												.filter((i) => !!i),
										};
										if (!generatedData[item.id]?.elements || generatedData[item.id].elements.length === 0) {
											delete generatedData[item.id];
										}
									});

									const textContent = JSON.stringify(generatedData, null, 2);
									const blob = new Blob([textContent], { type: "text/plain" });
									const url = URL.createObjectURL(blob);
									const a = document.createElement("a");
									a.href = url;
									a.download = "initial-mapping.json";
									document.body.appendChild(a);
									a.click();
								}}
							>
								Download initial Mapping for Mike
							</button>
						</div>
					)}
					<div className="h-[200px] ">
						<div className="w-full h-[52px]  py-4 justify-between items-center inline-flex">
							<div className="text-stone-400 text-[10px] font-medium font-['Hanken Grotesk'] leading-[14px]">All rights reserved to 2024</div>
							<div className="justify-center items-center gap-1 flex">
								<div className="w-5 h-5 justify-start items-start flex">
									<div className="w-5 h-5 relative bg-stone-100 rounded-lg border border-neutral-200" />
								</div>
								<div>
									<span className="text-stone-400 text-[10px] font-medium font-['Hanken Grotesk'] leading-3">Powered by </span>
									<span className="text-stone-400 text-[10px] font-bold font-['Hanken Grotesk'] leading-[14px]">Lasso</span>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			{showPreviewModal && (
				<SavePreviewModal
					lineItemToProcess={lineItemToProcess}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
					selectedScopes={selectedScopes}
					onScopesChange={setSelectedScopes}
					onCancel={() => setShowPreviewModal(false)}
					onSave={handleSaveLineItem}
				/>
			)}

			{showVariableCreationPopup && (
				<VariableCreationPopup
					position={showVariableCreationPopup}
					variablesWindowVariables={variablesWindowVariables}
					onVariablesChange={setVariablesWindowVariables}
					allowEmptyValue={allowEmptyValue}
					onAllowEmptyValueChange={setAllowEmptyValue}
					conditional={conditional}
					onConditionalChange={setConditional}
					defaultVariableValue={defaultVariableValue}
					onDefaultVariableValueChange={setDefaultVariableValue}
					useFixedPosition={!!lineItemToEdit}
					inputRef={inputVariableRef}
					onCancel={() => variableModalCallback("cancel")}
					onDelete={() => variableModalCallback("delete")}
					onSave={() => variableModalCallback("create")}
					lineItemToProcess={lineItemToProcess}
					editingIndex={editingIndex}
					onLineItemChange={setLineItemToProcess}
				/>
			)}

			{mappingStore.loadingData && <LoadingAnimation />}
			{selectedLineItemForInfo && <LineItemInfoModal lineItem={selectedLineItemForInfo} onClose={() => setSelectedLineItemForInfo(null)} />}
			{import.meta.env.VITE_XACTIMATE_SYNC_ENABLED === "true" && savedLineItemForSync && (
				<XactimateSyncReviewModal
					lineItem={savedLineItemForSync}
					onDismiss={handleSyncReviewDismiss}
					pendingNonDraftScopes={pendingNonDraftScopes.length > 0 ? pendingNonDraftScopes : undefined}
					onPendingScopesApplied={
						pendingNonDraftScopes.length > 0
							? () => applyPendingNonDraftScopes(savedLineItemForSync, pendingNonDraftScopes)
							: undefined
					}
				/>
			)}
		</div>
	);
});

export default LineItemDBCreationToolPage;
